import { useCallback, useEffect, useState } from "react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { ExternalLink, RefreshCw, Search, Share2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useProfiles } from "@/hooks/auth/useProfiles";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

interface SuiteVisitRow {
  id: string;
  created_at: string;
  user_id: string | null;
  resource_name: string | null;
  metadata: Record<string, unknown> | null;
}

interface AppSummary {
  appName: string;
  totalVisits: number;
  uniqueUsers: number;
}

const APP_COLORS: Record<string, string> = {
  Mail: "#4E9CFF",
  Sync: "#FF4DB8",
  Outbound: "#FFBB33",
};

const APP_ICONS: Record<string, GenieIconName> = {
  Mail: "mail",
  Sync: "sync",
  Outbound: "phone-outgoing",
};

const PAGE_SIZE = 50;

const SuiteVisitLogsPage = () => {
  const { data: profiles = [] } = useProfiles();

  const [rows, setRows] = useState<SuiteVisitRow[]>([]);
  const [summary, setSummary] = useState<AppSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterApp, setFilterApp] = useState<string>("all");
  const [filterUser, setFilterUser] = useState<string>("all");
  const [page, setPage] = useState(1);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      let query = supabase
        .from("activities")
        .select("id, created_at, user_id, resource_name, metadata")
        .eq("action", "suite_app_visited")
        .order("created_at", { ascending: false });

      if (filterApp !== "all") query = query.eq("resource_name", filterApp);
      if (filterUser !== "all") query = query.eq("user_id", filterUser);

      const { data, error } = await query.range(from, to);
      if (error) throw error;

      setRows((data as SuiteVisitRow[]) ?? []);
    } catch (err) {
      console.error("Failed to load suite visit logs:", err);
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [page, filterApp, filterUser]);

  const loadSummary = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("activities")
        .select("resource_name, user_id")
        .eq("action", "suite_app_visited");

      if (error || !data) return;

      const map: Record<string, { users: Set<string>; total: number }> = {};
      for (const row of data) {
        const key = row.resource_name ?? "Unknown";
        if (!map[key]) map[key] = { users: new Set(), total: 0 };
        map[key].total++;
        if (row.user_id) map[key].users.add(row.user_id);
      }

      setSummary(
        Object.entries(map).map(([appName, val]) => ({
          appName,
          totalVisits: val.total,
          uniqueUsers: val.users.size,
        })),
      );
    } catch (err) {
      console.error("Failed to load summary:", err);
    }
  }, []);

  useEffect(() => {
    loadData();
    loadSummary();
  }, [loadData, loadSummary]);

  const filtered = rows.filter((r) => {
    if (!searchTerm) return true;
    const email = profiles.find((p) => p.id === r.user_id)?.email ?? r.user_id ?? "";
    return (
      email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (r.resource_name ?? "").toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const getProfileEmail = (userId: string | null) => {
    if (!userId) return "—";
    return profiles.find((p) => p.id === userId)?.email ?? userId;
  };

  const getSource = (metadata: Record<string, unknown> | null) =>
    (metadata?.source as string) ?? "—";

  const getAppColor = (name: string | null) => APP_COLORS[name ?? ""] ?? "#8B9AAF";
  const getAppIcon = (name: string | null) => APP_ICONS[name ?? ""];

  return (
    <div className="min-h-screen bg-genie-background p-4 md:p-8">
      <div className="max-w-[1920px] mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <GenieIcon name="share" size={32} className="h-8 w-8" />
          <div>
            <h1 className="text-3xl font-bold text-genie-text-primary">Suite Visit Logs</h1>
            <p className="text-genie-text-secondary">Who has followed links to Suite apps and how many times</p>
          </div>
        </div>

        {/* Summary cards */}
        {summary.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            {summary.map((s) => (
              <div
                key={s.appName}
                className="bg-white rounded-xl border border-genie-border p-4 flex items-center gap-4"
              >
                <span
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${getAppColor(s.appName)}18` }}
                >
                  {getAppIcon(s.appName) ? (
                    <GenieIcon name={getAppIcon(s.appName)} size={20} />
                  ) : (
                    <Share2 className="h-5 w-5" style={{ color: getAppColor(s.appName) }} />
                  )}
                </span>
                <div>
                  <p className="font-bold text-genie-text-primary">{s.appName}</p>
                  <p className="text-sm text-genie-text-secondary">
                    <span className="font-semibold text-genie-text-primary">{s.totalVisits}</span> visits ·{" "}
                    <span className="font-semibold text-genie-text-primary">{s.uniqueUsers}</span> unique users
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg p-4 mb-6 border border-genie-border">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <Select value={filterApp} onValueChange={(v) => { setFilterApp(v); setPage(1); }}>
              <SelectTrigger>
                <SelectValue placeholder="All apps" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All apps</SelectItem>
                <SelectItem value="Mail">Mail</SelectItem>
                <SelectItem value="Sync">Sync</SelectItem>
                <SelectItem value="Outbound">Outbound</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterUser} onValueChange={(v) => { setFilterUser(v); setPage(1); }}>
              <SelectTrigger>
                <SelectValue placeholder="All users" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All users</SelectItem>
                {profiles.sort((a, b) => a.email.localeCompare(b.email)).map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-genie-text-muted" />
              <Input
                placeholder="Search by user or app..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-sm text-genie-text-secondary">
              {filtered.length} result{filtered.length !== 1 ? "s" : ""}
            </p>
            <Button
              onClick={() => { loadData(); loadSummary(); }}
              disabled={isLoading}
              variant="outline"
              size="sm"
              className="gap-2"
            >
              <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
              Reload
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-genie-border">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>App</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>App URL</TableHead>
                  <TableHead>Visited</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-genie-text-secondary">
                      <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-genie-primary" />
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-genie-text-muted">
                      No suite visits recorded yet
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getAppIcon(row.resource_name) && (
                            <span
                              className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: `${getAppColor(row.resource_name)}18` }}
                            >
                              <GenieIcon name={getAppIcon(row.resource_name)} size={14} />
                            </span>
                          )}
                          <span className="font-medium text-genie-text-primary">
                            {row.resource_name ?? "—"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-genie-text-secondary">
                        <CopyableEmail email={getProfileEmail(row.user_id)} />
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={cn(
                            "text-xs border w-fit",
                            getSource(row.metadata) === "sidebar"
                              ? "bg-genie-primary/10 text-genie-primary border-genie-primary/20"
                              : "bg-genie-background-muted text-genie-text-secondary border-genie-border",
                          )}
                        >
                          {getSource(row.metadata)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-genie-text-secondary">
                        {row.metadata?.appUrl ? (
                          <span className="flex items-center gap-1">
                            <span className="truncate max-w-[200px]">{row.metadata.appUrl as string}</span>
                            <ExternalLink className="h-3 w-3 flex-shrink-0" />
                          </span>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-genie-text-secondary whitespace-nowrap">
                        {new Date(row.created_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {rows.length >= PAGE_SIZE && (
            <div className="flex items-center justify-between gap-4 p-4 border-t border-genie-border text-sm">
              <p className="text-genie-text-secondary">
                Page <span className="font-semibold">{page}</span>
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={filtered.length < PAGE_SIZE}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuiteVisitLogsPage;
